import { datesEqual, formatCurrency, formatDateShort, formatNumber } from '@helpers';
import { SelectedFacet } from '@models';
import { searchService } from '@services';
import { BaseComponent } from '../base.component';
import defaultHtml from './selected-facets.component.hbs';

export class SelectedFacetsComponent extends BaseComponent<Array<SelectedFacet>> {
    protected override bindEvent = 'hawksearch:bind-selected-facets';
    protected override customTemplatePropertyName = 'selectedFacets';
    protected override defaultHtml = defaultHtml;

    protected getContentHtml(template: HandlebarsTemplateDelegate): string {
        if (!this.data?.length) {
            return '';
        }

        const selections: Array<{
            field: string;
            title: string;
            selectionTitle: string;
            selectionValue: string;
            excluded: boolean;
        }> = [];

        this.data.forEach((facet) => {
            facet.values.forEach((value) => {
                let selectionTitle = value.title;

                const dateRange = /^(?<min>\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z)?,(?<max>\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z)?$/.exec(value.value);
                const numericRange = /^(?:(?<min>\d+(?:\.\d+)?))?,(?:(?<max>\d+(?:\.\d+)?))?$/.exec(value.value);

                if (dateRange) {
                    const min = dateRange.groups?.min !== undefined ? new Date(dateRange.groups.min) : undefined;
                    const max = dateRange.groups?.max !== undefined ? new Date(dateRange.groups.max) : undefined;

                    if (min && max && datesEqual(min, max)) {
                        selectionTitle = formatDateShort(min);
                    } else if (min && max) {
                        selectionTitle = `${formatDateShort(min)} - ${formatDateShort(max)}`;
                    } else if (min) {
                        selectionTitle = `After ${formatDateShort(min)}`;
                    } else if (max) {
                        selectionTitle = `Before ${formatDateShort(max)}`;
                    }
                }
                if (numericRange) {
                    const min = numericRange.groups?.min !== undefined ? parseFloat(numericRange.groups.min) : undefined;
                    const max = numericRange.groups?.max !== undefined ? parseFloat(numericRange.groups.max) : undefined;
                    const formatValue = facet.currency ? formatCurrency : formatNumber;

                    if (min !== undefined && max !== undefined) {
                        if (min === max) {
                            selectionTitle = formatValue(min, 0);
                        } else {
                            selectionTitle = `${formatValue(min, 0)} - ${formatValue(max, 0)}`;
                        }
                    } else if (min !== undefined) {
                        selectionTitle = `${formatValue(min, 0)} and up`;
                    } else if (max !== undefined) {
                        selectionTitle = `${formatValue(max, 0)} and below`;
                    }
                }

                selections.push({
                    field: facet.field,
                    title: facet.title,
                    selectionTitle: selectionTitle,
                    selectionValue: value.value,
                    excluded: value.excluded
                });
            });
        });

        selections.sort((x, y) => {
            if (x.field == y.field) {
                return x.selectionTitle.localeCompare(y.selectionTitle);
            }

            return x.title.localeCompare(y.title);
        });

        const html = template({
            facets: this.data,
            selections: selections
        });

        return html;
    }

    protected bindChildElements(): void {
        this.shadowRoot?.querySelectorAll('[hawksearch-facet-field][hawksearch-facet-value]').forEach((e) => {
            e.addEventListener('click', (event: Event) => {
                event.preventDefault();

                const element = event.currentTarget as HTMLElement;
                const field = element.getAttribute('hawksearch-facet-field');
                const value = element.getAttribute('hawksearch-facet-value');

                if (!(field && value)) {
                    return;
                }

                searchService.removeFacetValue(field, value);
            });
        });
    }
}
